import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';

import { cooperplaceApi } from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';

import { formatDate, formatNumber, formatPlate } from 'v3/utils/formatter';
import { useVehicleBodies } from '../shared/context';
import { validate } from '../shared/validator';
import { Section } from '../shared/Section';
import { Data as Inputs } from '../shared/Data';
import { Editable } from '../shared/Editable';

export function Data() {
  const vehicleBody = useVehicleBodies();
  const snackbar = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  async function saveData() {
    setLoading(true);
    const isDataValid = await validate(
      'edit',
      vehicleBody.data,
      vehicleBody.setErrors
    );

    if (!isDataValid) {
      snackbar.show(<Text>Verifique os campos obrigatórios!</Text>, {
        type: 'error',
      });
      setLoading(false);
      return;
    }

    try {
      const payload = {
        plate: vehicleBody?.data?.plate,
        yearModel: vehicleBody?.data?.yearModel,
        yearManufacture: vehicleBody?.data?.yearManufacture,
        chassi: vehicleBody?.data?.chassi,
        renavam: vehicleBody?.data?.renavam,
        antt: vehicleBody?.data?.antt,
        rntrcExpDate: vehicleBody?.data?.rntrcExpDate,
        totalGrossWeight: vehicleBody?.data?.totalGrossWeight,
        tareMass: vehicleBody?.data?.tareMass,
        cubage: vehicleBody?.data?.cubage,
        brand: vehicleBody?.data?.brand?.name,
        color: vehicleBody?.data?.color?.label,
        plateCityId: vehicleBody?.data?.city?.id,
        plateCity: vehicleBody?.data?.city?.name,
        plateState: vehicleBody?.data?.city?.province?.uf,
        model: vehicleBody?.data?.model?.label,
        anttType: vehicleBody?.data?.anttType?.id,
        manufacturerId: vehicleBody?.data?.brand?.id,
        vehicleTypeId: vehicleBody?.data?.vehicleType?.id,
        vehicleBodyTypeId: vehicleBody?.data?.bodyType?.id,
        anttAdherence: vehicleBody?.data?.anttAdherence,
        rntrcValidated: vehicleBody?.data?.rntrcValidated,
      };

      const { data } = await cooperplaceApi.put(
        `/vehicleBodies/${vehicleBody?.data?.id}`,
        payload
      );

      vehicleBody.setData({ rntrcValidated: data?.rntrcValidated });

      vehicleBody.updatePrevVehicleBody({
        ...vehicleBody.data,
        rntrcValidated: data?.rntrcValidated,
      });

      snackbar.show(<Text>Carroceria salva com sucesso</Text>, {
        type: 'success',
      });

      setEditing(false);
    } catch (error) {
      const message = error?.response?.data?.message || 'Erro ao salvar';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Section
      Title="Dados da Carroceria"
      HeaderRightComponent={
        <Editable
          isEditing={editing}
          setEditing={setEditing}
          onSave={saveData}
          isLoading={loading}
        />
      }
      loading={vehicleBody?.loading}
    >
      {editing ? (
        <Inputs />
      ) : (
        <Row>
          <Col xs={6}>
            <Text type="label" color="#464E5F">
              Cidade - Estado
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.city?.name && vehicleBody?.data?.city?.province?.uf
                ? `${vehicleBody?.data?.city?.name} - ${vehicleBody?.data?.city?.province?.uf}`
                : vehicleBody?.data?.city?.name
                  ? vehicleBody?.data?.city?.name
                  : 'Não informado'}
            </Text>
          </Col>
          <Col xs={6}>
            <Text type="label" color="#464E5F">
              Tipo de Veículo
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.vehicleType?.name || 'Não informado'}
            </Text>
          </Col>
          <Col xs={6}>
            <Text type="label" color="#464E5F">
              Tipo de Carroceria
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.bodyType?.name || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Placa
            </Text>
            <Text type="regular" weight="800" color="black" as="p" className="mt-2">
              {formatPlate(vehicleBody?.data?.plate)}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Renavam
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.renavam || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Tipo de RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.anttType?.name || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.antt || 'Não informado'}
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <Text type="label" color="#464E5F">
              Data de emissão RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {formatDate(vehicleBody?.data?.anttAdherence, { isUTC: true }) ||
                'Não informado'}
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <Text type="label" color="#464E5F">
              Validade RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {formatDate(vehicleBody?.data?.rntrcExpDate, { isUTC: true }) ||
                'Não informado'}
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <Text type="label" color="#464E5F">
              RNTRC validada pelo GR
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.rntrcValidated ? 'Sim' : 'Não'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Chassi
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.chassi || 'Não informado'}
            </Text>
          </Col>

          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Marca
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.brand?.name || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Modelo
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.model?.label || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Ano do modelo
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.yearModel || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Ano de fabricação
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.yearManufacture || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Cor
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.color?.label || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              PBT (Peso bruto total) em kg
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.totalGrossWeight
                ? formatNumber(vehicleBody?.data?.totalGrossWeight)
                : 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Tara em kg
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.tareMass
                ? formatNumber(vehicleBody?.data?.tareMass)
                : 'Não Informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="#464E5F">
              Cubagem em m³
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {vehicleBody?.data?.cubage
                ? formatNumber(vehicleBody?.data?.cubage)
                : 'Não informado'}
            </Text>
          </Col>
        </Row>
      )}
    </Section>
  );
}
